// src/components/rcv/ConsultaRcvTab.jsx (refactorizado)
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip
} from '@mui/material';
import {
  SearchOutlined,
  SaveOutlined,
  CancelOutlined,
  Visibility
} from '@mui/icons-material';

// Componentes compartidos
import { CardContainer, LoadingState, DataTable, ConfirmDialog } from '../shared';

// Hooks personalizados
import useApi from '../../hooks/useApi';
import useForm from '../../hooks/useForm';
import useNotification from '../../hooks/useNotification';

// Servicios y utilidades
import { siiService } from '../../services/siiService';
import { documentosService } from '../../services/documentosService';
import { formatearRut, formatearMonto, formatearFecha, getTipoDocLabel } from '../../utils/formatters';
import DocumentoButton from './DocumentoButton';
import { useCallback } from 'react';

// Configuración de reCAPTCHA
const RECAPTCHA_SITE_KEY = '6Lfmf9oqAAAAAI0DQfiyOlcu1_jpdL2Ngm02Ux47';

const ConsultaRcvTab = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  
  // Estados Redux
  const { tokenSII } = useSelector((state) => state.auth);
  const { empresaActiva } = useSelector((state) => state.empresa);

  // Estado del componente
  const [periodos, setPeriodos] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [documentosSeleccionados, setDocumentosSeleccionados] = useState({});
  const [documentosExistentes, setDocumentosExistentes] = useState({});
  const [isRecaptchaLoaded, setIsRecaptchaLoaded] = useState(false);
  const [loadingConsulta, setLoadingConsulta] = useState(false);
  const [cooldownSecs, setCooldownSecs] = useState(0);
  const cooldownRef = useRef(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  // Form para consulta
  const consultaForm = useForm({
    rutEmpresa: empresaActiva?.rut || '',
    dvEmpresa: empresaActiva?.dv || '',
    periodo: '',
    tipoOperacion: 'COMPRA',
    estadoContab: 'REGISTRO'
  });

  // API hooks
  const { loading: loadingCarga, error: errorCarga, execute: cargarDocumentos } = 
    useApi(documentosService.cargarDocumentos);
  
  const { loading: loadingVerificar, execute: verificarDocumentos } = 
    useApi(documentosService.getDocumentosPeriodo);

  // Cargar reCAPTCHA - Comentado temporalmente por CSP
  useEffect(() => {
    // TODO: Revisar configuración CSP para permitir scripts de reCAPTCHA
    // const loadRecaptcha = () => {
    //   const script = document.createElement('script');
    //   script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    //   script.addEventListener('load', () => {
    //     window.grecaptcha.ready(() => {
    //       setIsRecaptchaLoaded(true);
    //     });
    //   });
    //   document.body.appendChild(script);
    // };
    
    // loadRecaptcha();
    
    // return () => {
    //   // Limpiar script si es necesario
    //   const scripts = document.querySelectorAll(`script[src*="recaptcha"]`);
    //   scripts.forEach(s => s.remove());
    // };
    
    // Por ahora, marcamos como cargado para permitir la funcionalidad
    setIsRecaptchaLoaded(true);
  }, []);

  // Generar periodos
  useEffect(() => {
    // Importar la función de generación de períodos
    import('../../utils/periodos').then(({ generarPeriodos }) => {
      const opciones = generarPeriodos();
      setPeriodos(opciones);
      if (opciones.length > 0) {
        consultaForm.setValues(prev => ({ ...prev, periodo: opciones[0].value }));
      }
    });
  }, []);

  // Establecer valores predeterminados de la empresa activa y limpiar resultados anteriores
  useEffect(() => {
    if (empresaActiva) {
      consultaForm.setValues(prev => ({
        ...prev,
        rutEmpresa: empresaActiva.rut,
        dvEmpresa: empresaActiva.dv
      }));
      // Limpiar documentos de la consulta anterior al cambiar de empresa
      setDocumentos([]);
      setDocumentosSeleccionados({});
      setDocumentosExistentes({});
    }
  }, [empresaActiva]);

  // Verificar documentos existentes
  const verificarDocumentosExistentes = useCallback(async () => {
    if (!consultaForm.values.rutEmpresa || !consultaForm.values.periodo || documentos.length === 0) return;

    try {
      const existentes = await verificarDocumentos(
        consultaForm.values.rutEmpresa,
        consultaForm.values.periodo,
        consultaForm.values.tipoOperacion
      );
      
      // Crear mapa de documentos existentes
      const mapaExistentes = {};
      
      const esVenta = consultaForm.values.tipoOperacion === 'VENTA';
      documentos.forEach((doc, index) => {
        const existe = esVenta
          ? existentes.some(e => e.rut_cliente === doc.rut_cliente && e.folio === doc.folio)
          : existentes.some(e => e.rut_proveedor === doc.rut_proveedor && e.folio === doc.folio);
        
        if (existe) {
          mapaExistentes[index.toString()] = true;
        }
      });
      
      setDocumentosExistentes(mapaExistentes);
    } catch (error) {
      console.error("Error al verificar documentos existentes:", error);
    }
  }, [consultaForm.values.rutEmpresa, consultaForm.values.periodo, documentos, verificarDocumentos]);

  // Ejecutar verificación cuando cambian los documentos
  useEffect(() => {
    if (documentos.length > 0) {
      verificarDocumentosExistentes();
    }
  }, [documentos, verificarDocumentosExistentes]);

  useEffect(() => () => clearInterval(cooldownRef.current), []);

  const startCooldown = (secs = 5) => {
    setCooldownSecs(secs);
    clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldownSecs(s => {
        if (s <= 1) { clearInterval(cooldownRef.current); return 0; }
        return s - 1;
      });
    }, 1000);
  };

  // Manejar consulta
  const handleConsultar = async (e) => {
    e.preventDefault();
    
    if (!consultaForm.validateForm()) return;
    
    if (!tokenSII) {
      showNotification('Se requiere tokenSII para consultar datos del SII', 'error');
      return;
    }

    setLoadingConsulta(true);
    setDocumentos([]);
    setDocumentosSeleccionados({});
    setDocumentosExistentes({});

    try {
      let recaptchaToken = 'dummy_token';
      if (isRecaptchaLoaded && window.grecaptcha && window.grecaptcha.execute) {
        try {
          recaptchaToken = await window.grecaptcha.execute(
            RECAPTCHA_SITE_KEY,
            { action: 'rcv_consulta' }
          );
        } catch (error) {
          console.error("Error obteniendo token reCAPTCHA:", error);
          // Usar token dummy si hay error
          recaptchaToken = 'dummy_token';
        }
      }

      // Consultar RCV
      const responseData = await siiService.consultarRcv({
        rutEmisor: consultaForm.values.rutEmpresa,
        dvEmisor: consultaForm.values.dvEmpresa,
        periodo: consultaForm.values.periodo,
        tipoOperacion: consultaForm.values.tipoOperacion,
        estadoContab: consultaForm.values.estadoContab,
        recaptchaToken: recaptchaToken,
        conversationId: tokenSII
      });

      // Procesar y guardar documentos
      const docs = responseData?.documentos ?? [];
      setDocumentos(docs);
      if (docs.length === 0) {
        showNotification(
          'La consulta fue exitosa pero el SII no retornó documentos. Verifique el período, el tipo de operación y el estado del token.',
          'warning'
        );
      } else {
        showNotification(`Consulta exitosa. Se encontraron ${docs.length} documentos.`, 'success');
      }
    } catch (error) {
      showNotification(`Error en la consulta: ${error.message || 'Error desconocido'}`, 'error');
    } finally {
      setLoadingConsulta(false);
      startCooldown(5);
    }
  };

  // Manejar carga de documentos
  const handleCargarDocumentos = async () => {
    // Obtener solo los documentos seleccionados que no existen en la BD
    const docsParaCargar = Object.keys(documentosSeleccionados)
      .filter(id => documentosSeleccionados[id] && !documentosExistentes[id])
      .map(id => {
        const index = parseInt(id); // Convertir string a número
        const doc = documentos[index]; // Usar índice numérico
        return doc;
      })
      .filter(doc => doc !== undefined); // Filtrar documentos no encontrados

    if (docsParaCargar.length === 0) {
      showNotification('No hay documentos seleccionados para cargar o todos ya existen en la base de datos', 'warning');
      return;
    }

    try {
      // Cargar documentos seleccionados
      await cargarDocumentos({
        rut_empresa: consultaForm.values.rutEmpresa,
        dv_empresa: consultaForm.values.dvEmpresa,
        periodo: consultaForm.values.periodo,
        tipo_operacion: consultaForm.values.tipoOperacion,
        estado_contab: consultaForm.values.estadoContab,
        documentos_seleccionados: docsParaCargar
      });
      
      showNotification(`Se cargaron ${docsParaCargar.length} documentos a la base de datos.`, 'success');
      
      // Actualizar estado de documentos existentes
      const nuevosExistentes = {...documentosExistentes};
      docsParaCargar.forEach((doc) => {
        const docIndex = documentos.findIndex(d => 
          d.rut_proveedor === doc.rut_proveedor && 
          d.folio === doc.folio
        );
        if (docIndex >= 0) {
          nuevosExistentes[docIndex.toString()] = true;
        }
      });
      setDocumentosExistentes(nuevosExistentes);
      
      // Limpiar selecciones
      setDocumentosSeleccionados({});
      
      // Navegar a la página de documentos después de la carga exitosa
      setTimeout(() => {
        navigate('/rcv/documentos');
      }, 1500);
    } catch (error) {
      showNotification(`Error al cargar documentos: ${error.message || 'Error desconocido'}`, 'error');
    }
  };

  // Contar documentos
  const contarDocumentos = () => {
    const total = documentos.length;
    const existentes = Object.keys(documentosExistentes).length;
    const nuevos = total - existentes;
    const seleccionados = Object.keys(documentosSeleccionados).filter(k => documentosSeleccionados[k]).length;
    
    return { total, existentes, nuevos, seleccionados };
  };

  const { total, existentes, nuevos, seleccionados } = contarDocumentos();

  const esVenta = consultaForm.values.tipoOperacion === 'VENTA';

  // Configuración de columnas para la tabla
  const tableColumns = [
    {
      field: 'tipo_doc',
      headerName: 'Tipo',
      renderCell: (val) => <Chip label={getTipoDocLabel(val || '')} size="small" variant="outlined" />
    },
    {
      field: esVenta ? 'rut_cliente' : 'rut_proveedor',
      headerName: esVenta ? 'RUT Cliente' : 'RUT Proveedor',
      renderCell: (val, row) => formatearRut(val || '', esVenta ? (row?.dv_cliente || '') : (row?.dv_proveedor || ''))
    },
    {
      field: 'razon_social',
      headerName: 'Razón Social',
      renderCell: (val) => val || '-'
    },
    {
      field: 'folio',
      headerName: 'Folio',
      renderCell: (val) => val || '-'
    },
    {
      field: 'fecha_docto',
      headerName: 'Fecha',
      renderCell: (val) => val ? formatearFecha(val) : '-'
    },
    {
      field: 'monto_total',
      headerName: 'Monto',
      align: 'right',
      renderCell: (val) => val !== null && val !== undefined ? formatearMonto(val) : '-'
    },
    {
      field: 'estado',
      headerName: 'Estado',
      align: 'center',
      renderCell: (_, row) => {
        const indexStr = row.id !== undefined ? row.id.toString() : '0';
        const isExisting = documentosExistentes[indexStr];
        return (
          <Chip
            size="small"
            label={isExisting ? "Ya existe" : "Nuevo"}
            color={isExisting ? "default" : "success"}
            variant={isExisting ? "filled" : "outlined"}
          />
        );
      }
    }
  ];

  // Acciones adicionales para documentos
  const documentActions = [
    {
      icon: <Visibility fontSize="small" />,
      tooltip: 'Ver/Subir documento',
      onClick: (row, index) => {
        // Aquí podríamos abrir el diálogo de documento directamente
        // Por ahora, usaremos el DocumentoButton pero sin el wrapper
      },
      customRender: (row, index) => (
        <DocumentoButton 
          key={`doc-${index}`}
          rutEmisor={row.rut_proveedor}
          tipoDoc={row.tipo_doc}
          folio={row.folio}
          movimientoId={row.id}
          soloIcono={true}
          onDocumentoCreado={() => {
            // Recargar datos si es necesario
          }}
        />
      )
    }
  ];

  return (
    <CardContainer
      title="Consulta RCV"
      subheader="Consulte el Registro de Compras y Ventas del SII"
    >
        <form onSubmit={handleConsultar}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={9}>
              <TextField
                label="RUT Empresa"
                placeholder="Ej: 77123456"
                fullWidth
                value={consultaForm.values.rutEmpresa}
                disabled={true} // Campo deshabilitado
                inputProps={{ maxLength: 9 }}
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="DV"
                placeholder="Ej: K"
                fullWidth
                value={consultaForm.values.dvEmpresa}
                disabled={true} // Campo deshabilitado
                inputProps={{ maxLength: 1 }}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="periodo-label">Período</InputLabel>
                <Select
                  labelId="periodo-label"
                  id="periodo"
                  name="periodo"
                  value={consultaForm.values.periodo}
                  label="Período"
                  onChange={consultaForm.handleChange}
                >
                  {periodos.map((opcion) => (
                    <MenuItem key={opcion.value} value={opcion.value}>
                      {opcion.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="tipo-operacion-label">Tipo Operación</InputLabel>
                <Select
                  labelId="tipo-operacion-label"
                  id="tipoOperacion"
                  name="tipoOperacion"
                  value={consultaForm.values.tipoOperacion}
                  label="Tipo Operación"
                  onChange={consultaForm.handleChange}
                >
                  <MenuItem value="COMPRA">Compra</MenuItem>
                  <MenuItem value="VENTA">Venta</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="estado-contab-label">Estado Contable</InputLabel>
                <Select
                  labelId="estado-contab-label"
                  id="estadoContab"
                  name="estadoContab"
                  value={consultaForm.values.estadoContab}
                  label="Estado Contable"
                  onChange={consultaForm.handleChange}
                >
                  <MenuItem value="REGISTRO">Registro</MenuItem>
                  <MenuItem value="PENDIENTE">Pendiente</MenuItem>
                  <MenuItem value="NO_INCLUIR">No Incluir</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loadingConsulta || cooldownSecs > 0 || !tokenSII}
                startIcon={<SearchOutlined />}
              >
                {loadingConsulta ? 'Consultando...' : cooldownSecs > 0 ? `Espere ${cooldownSecs}s...` : 'Consultar RCV'}
              </Button>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                disabled={loadingCarga || seleccionados === 0}
                startIcon={<SaveOutlined />}
                onClick={() => setOpenConfirmDialog(true)}
              >
                {loadingCarga ? 'Cargando...' : `Cargar ${seleccionados} documento(s) seleccionado(s)`}
              </Button>
            </Grid>
          </Grid>
        </form>

        <LoadingState loading={loadingVerificar} error={errorCarga}>
          {/* Tabla con documentos consultados */}
          {documentos.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Chip label={`Total: ${total}`} color="primary" variant="outlined" />
                <Chip label={`Existentes: ${existentes}`} color="default" variant="outlined" />
                <Chip label={`Nuevos: ${nuevos}`} color="success" variant="outlined" />
                <Chip label={`Seleccionados: ${seleccionados}`} color="secondary" variant="outlined" />
              </Box>
              
              <DataTable
                columns={tableColumns}
                data={documentos.map((doc, index) => ({ ...doc, id: index }))}
                actions={documentActions}
                selectable={true}
                onRowSelect={(ids) => {
                  const newSelection = {};
                  ids.forEach(id => {
                    const indexStr = id.toString();
                    if (!documentosExistentes[indexStr]) {
                      newSelection[indexStr] = true;
                    }
                  });
                  setDocumentosSeleccionados(newSelection);
                }}
              />
              
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelOutlined />}
                  onClick={() => {
                    setDocumentos([]);
                    setDocumentosSeleccionados({});
                    setDocumentosExistentes({});
                  }}
                >
                  Cancelar
                </Button>
                
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<SaveOutlined />}
                  onClick={() => setOpenConfirmDialog(true)}
                  disabled={loadingCarga || seleccionados === 0}
                >
                  {loadingCarga ? 'Cargando...' : `Cargar ${seleccionados} documento(s)`}
                </Button>
              </Box>
            </Box>
          )}
        </LoadingState>

      {/* Diálogo de confirmación */}
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={handleCargarDocumentos}
        title="Confirmar Carga de Documentos"
        message={`¿Está seguro que desea cargar ${seleccionados} documento(s) al sistema?`}
        loading={loadingCarga}
      />
    </CardContainer>
  );
};

export default ConsultaRcvTab;